import {
  safeGetItemFromLocalStorage,
  safeSaveItemToLocalStorage
} from "./storage/utils";
import moment from "moment";
import userReducer from "./user/reducers";
import { compress, decompress } from "lz-string";

const noOp = a => a;

function generateExpiration(seconds) {
  return moment()
    .add(seconds, "seconds")
    .utc()
    .format();
}

function hasUser(user) {
  return user && user.hasFetched && user.info && !!user.info.email;
}

function getKey(user) {
  return user.info.email.hashMe();
}

export function createPersistedReducer({
  reducer,
  storageKey,
  expiresSec = 24 * 60 * 60, // default 1 day
  stateToStorage = noOp, // args data, user. return undefined = do not store
  storageToState = noOp // args data, user. return undefined = do not restore
}) {
  let user = null;

  return (state, action) => {
    try {
      if (!storageKey) {
        console.error(
          "createPersistedReducer: no storage key provided. persistence disabled."
        );
        return reducer(state, action);
      }

      // If we're pointing to prod, do not store or restore data.
      // This helps with issues like seeing the cached dev book list
      // when you really need the prod one.
      if (window.pointingAtProd) {
        return reducer(state, action);
      }

      const lastUser = user;
      user = userReducer(user, action);

      // If the user has disappeared, clear data
      if (hasUser(lastUser) && !hasUser(user)) {
        return reducer(undefined, action);
      }

      // If a user has appeared, try to restore data
      const data = safeGetItemFromLocalStorage(storageKey);
      if (!hasUser(lastUser) && hasUser(user) && data) {
        const storedData = JSON.parse(decompress(data));
        if (
          typeof storedData.value !== "undefined" &&
          storedData.key === getKey(user)
        ) {
          const result = storageToState(storedData.value, user);
          if (typeof result !== "undefined") {
            return result;
          }
        }
      }

      // Try to store changed data
      const result = reducer(state, action);
      if (hasUser(user) && result !== state) {
        const value = stateToStorage(result, user);
        if (typeof value !== "undefined") {
          // Keep only one copy ever due to size, but keyed by user for restore
          safeSaveItemToLocalStorage(
            storageKey,
            compress(
              JSON.stringify({
                value,
                // Expires not implemented yet, but track now for later use
                expires: generateExpiration(expiresSec),
                key: getKey(user)
              })
            )
          );
        }
      }
      return result;
    } catch (e) {
      // Persistence-related exceptions should not blow everything up
      console.error(e);
      return reducer(state, action);
    }
  };
}
